import { supabase } from "@/lib/supabase";
import { SystemUpdateService } from '@/services/system-update.service';

export type InvoiceStatus =
  | "draft"
  | "issued"
  | "partially_paid"
  | "paid"
  | "overdue"
  | "void";

export type PaymentMethod =
  | "cash"
  | "check"
  | "bank_transfer"
  | "credit_card"
  | "debit_card"
  | "online"
  | "other";

export interface InvoiceRow {
  id: string;
  organization_id: string;
  customer_id: string;
  job_id: string | null;
  quote_id: string | null;
  invoice_number: string | null;
  status: InvoiceStatus;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  amount_paid: number;
  balance_due: number;
  issued_at: string | null;
  due_date: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  jobs?: {
    id: string;
    job_number: string | null;
    title: string | null;
  } | null;
  customers?: {
    id: string;
    full_name: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
}

export interface InvoiceItemRow {
  id: string;
  invoice_id: string;
  sort_order: number;
  item_type: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  created_at: string;
}

export interface PaymentRow {
  id: string;
  organization_id: string;
  invoice_id: string;
  customer_id: string | null;
  payment_number: string | null;
  amount: number;
  method: PaymentMethod;
  reference: string | null;
  paid_at: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountingKpis {
  totalJobs: number;
  activeJobs: number;
  totalLeads: number;
  rejectedLeads: number;
  avgRejectRate: number;
  totalQuotes: number;
  wonQuotes: number;
  wonQuoteRevenue: number;
  totalInvoices: number;
  openInvoices: number;
  overdueInvoices: number;
  invoiceRevenue: number;
  outstandingBalance: number;
}

export interface CreateInvoiceForJobInput {
  jobId: string;
  type: "deposit" | "progress" | "final";
  amount: number;
  status?: "draft" | "issued";
  dueDate?: string | null;
  issuedAt?: string | null;
  notes?: string | null;
  description?: string | null;
}

export const AccountingService = {
  async listInvoicesByJob(jobId: string): Promise<{ data: InvoiceRow[]; error: unknown }> {
    const { data, error } = await supabase
      .from("invoices")
      .select(`
        id,
        organization_id,
        customer_id,
        job_id,
        quote_id,
        invoice_number,
        status,
        subtotal,
        tax,
        discount,
        total,
        amount_paid,
        balance_due,
        issued_at,
        due_date,
        paid_at,
        notes,
        created_at,
        updated_at,
        jobs:job_id (
          id,
          job_number,
          title
        ),
        customers:customer_id (
          id,
          full_name,
          first_name,
          last_name,
          email
        )
      `)
      .eq("job_id", jobId)
      .order("created_at", { ascending: true });

    return { data: (data ?? []) as InvoiceRow[], error };
  },

  async listInvoices(params?: {
    page?: number;
    pageSize?: number;
    status?: "all" | InvoiceStatus;
    search?: string;
  }): Promise<{ data: InvoiceRow[]; count: number; error: unknown }> {
    const page = Math.max(1, params?.page ?? 1);
    const pageSize = Math.max(1, params?.pageSize ?? 25);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const status = params?.status ?? "all";
    const search = (params?.search ?? "").trim();

    let query = supabase
      .from("invoices")
      .select(`
        id,
        organization_id,
        customer_id,
        job_id,
        quote_id,
        invoice_number,
        status,
        subtotal,
        tax,
        discount,
        total,
        amount_paid,
        balance_due,
        issued_at,
        due_date,
        paid_at,
        notes,
        created_at,
        updated_at,
        jobs:job_id (
          id,
          job_number,
          title
        ),
        customers:customer_id (
          id,
          full_name,
          first_name,
          last_name,
          email
        )
      `, { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (status !== "all") query = query.eq("status", status);
    if (search) query = query.ilike("invoice_number", `%${search}%`);

    const { data, error, count } = await query;
    return { data: (data ?? []) as InvoiceRow[], count: count ?? 0, error };
  },

  async listInvoiceItems(invoiceIds: string[]): Promise<{ data: InvoiceItemRow[]; error: unknown }> {
    if (invoiceIds.length === 0) return { data: [], error: null };
    const { data, error } = await supabase
      .from("invoice_items")
      .select("id, invoice_id, sort_order, item_type, description, quantity, unit_price, line_total, created_at")
      .in("invoice_id", invoiceIds)
      .order("sort_order", { ascending: true });

    return { data: (data ?? []) as InvoiceItemRow[], error };
  },

  async listPayments(invoiceIds: string[]): Promise<{ data: PaymentRow[]; error: unknown }> {
    if (invoiceIds.length === 0) return { data: [], error: null };
    const { data, error } = await supabase
      .from("payments")
      .select("id, organization_id, invoice_id, customer_id, payment_number, amount, method, reference, paid_at, notes, created_at, updated_at")
      .in("invoice_id", invoiceIds)
      .order("paid_at", { ascending: false });

    return { data: (data ?? []) as PaymentRow[], error };
  },

  async createInvoiceForJob(input: CreateInvoiceForJobInput): Promise<{ invoice: InvoiceRow | null; error: unknown }> {
    const amount = Number(input.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return { invoice: null, error: new Error("Invoice amount must be greater than zero.") };
    }

    const { data: jobRow, error: jobError } = await supabase
      .from("jobs")
      .select("id, job_number, title, organization_id, customer_id, metadata")
      .eq("id", input.jobId)
      .maybeSingle();

    if (jobError || !jobRow) {
      return { invoice: null, error: jobError ?? new Error("Job not found for invoice creation.") };
    }

    let customerId = jobRow.customer_id as string | null;
    let quoteId: string | null = null;

    const metadata = (jobRow.metadata as Record<string, unknown> | null) ?? null;
    const convertedFromQuoteId = typeof metadata?.convertedFromQuoteId === "string"
      ? metadata.convertedFromQuoteId.trim()
      : "";

    if (!customerId) {
      if (convertedFromQuoteId) {
        const byQuoteId = await supabase
          .from("quotes")
          .select("id, customer_id")
          .eq("id", convertedFromQuoteId)
          .maybeSingle();
        if (!byQuoteId.error && byQuoteId.data?.customer_id) {
          customerId = byQuoteId.data.customer_id;
          quoteId = byQuoteId.data.id;
        } else {
          const byQuoteNumber = await supabase
            .from("quotes")
            .select("id, customer_id")
            .eq("quote_number", convertedFromQuoteId)
            .maybeSingle();
          if (!byQuoteNumber.error && byQuoteNumber.data?.customer_id) {
            customerId = byQuoteNumber.data.customer_id;
            quoteId = byQuoteNumber.data.id;
          }
        }
      }

      if (!customerId) {
        const byLinkedJob = await supabase
          .from("quotes")
          .select("id, customer_id")
          .eq("linked_job_id", input.jobId)
          .limit(1)
          .maybeSingle();
        if (!byLinkedJob.error && byLinkedJob.data?.customer_id) {
          customerId = byLinkedJob.data.customer_id;
          quoteId = byLinkedJob.data.id;
        }
      }
    } else if (convertedFromQuoteId) {
      const quoteResult = await supabase
        .from("quotes")
        .select("id")
        .eq("id", convertedFromQuoteId)
        .maybeSingle();
      if (!quoteResult.error && quoteResult.data?.id) {
        quoteId = quoteResult.data.id;
      }
    }

    // Fallback for migrated jobs: resolve or create a customer from metadata.customerName.
    // Migrated Jobber imports may have no linked customer_id — this bridges the gap without blocking invoicing.
    if (!customerId) {
      const rawName = typeof metadata?.customerName === "string"
        ? (metadata.customerName as string).trim()
        : "";
      if (rawName) {
        const { data: found } = await supabase
          .from("customers")
          .select("id")
          .eq("organization_id", jobRow.organization_id)
          .ilike("full_name", rawName)
          .limit(1)
          .maybeSingle();
        if (found?.id) {
          customerId = found.id;
        } else {
          const parts = rawName.split(/\s+/);
          const firstName = parts[0];
          const lastName = parts.length > 1 ? parts.slice(1).join(" ") : null;
          const { data: newCustomer } = await supabase
            .from("customers")
            .insert({ organization_id: jobRow.organization_id, first_name: firstName, last_name: lastName })
            .select("id")
            .single();
          if (newCustomer?.id) {
            customerId = newCustomer.id;
            // Backfill customer_id on the job so future invoice/service lookups are instant
            await supabase.from("jobs").update({ customer_id: customerId }).eq("id", input.jobId);
          }
        }
      }
    }

    if (!customerId) {
      return { invoice: null, error: new Error("Unable to resolve customer for invoice creation.") };
    }

    const nowIso = new Date().toISOString();
    // Finance can backdate migrated invoices from legacy systems.
    const issuedAt = input.issuedAt ? new Date(input.issuedAt).toISOString() : nowIso;
    const dueDate = input.dueDate ?? null;
    const status = input.status ?? "draft";
    const itemDescription = input.description ?? `${input.type[0].toUpperCase()}${input.type.slice(1)} invoice`;

    const { data: invoice, error: invoiceError } = await supabase
      .from("invoices")
      .insert({
        organization_id: jobRow.organization_id,
        customer_id: customerId,
        job_id: jobRow.id,
        quote_id: quoteId,
        status,
        subtotal: amount,
        tax: 0,
        discount: 0,
        total: amount,
        amount_paid: 0,
        balance_due: amount,
        issued_at: issuedAt,
        due_date: dueDate,
        notes: input.notes?.trim() || null,
      })
      .select(`
        id,
        organization_id,
        customer_id,
        job_id,
        quote_id,
        invoice_number,
        status,
        subtotal,
        tax,
        discount,
        total,
        amount_paid,
        balance_due,
        issued_at,
        due_date,
        paid_at,
        notes,
        created_at,
        updated_at,
        jobs:job_id (
          id,
          job_number,
          title
        ),
        customers:customer_id (
          id,
          full_name,
          first_name,
          last_name,
          email
        )
      `)
      .single();

    if (invoiceError || !invoice) {
      return { invoice: null, error: invoiceError ?? new Error("Failed to create invoice.") };
    }

    const { error: itemError } = await supabase
      .from("invoice_items")
      .insert({
        invoice_id: invoice.id,
        sort_order: 1,
        item_type: input.type,
        description: itemDescription,
        quantity: 1,
        unit_price: amount,
      });

    if (itemError) {
      // Some environments have stricter `item_type` constraints.
      // Keep the invoice row and retry with a null item_type so financial flow is not blocked.
      const { error: fallbackItemError } = await supabase
        .from("invoice_items")
        .insert({
          invoice_id: invoice.id,
          sort_order: 1,
          item_type: null,
          description: itemDescription,
          quantity: 1,
          unit_price: amount,
        });
      if (fallbackItemError) {
        return { invoice: invoice as InvoiceRow, error: fallbackItemError };
      }
    }

    return { invoice: invoice as InvoiceRow, error: null };
  },

  async updateInvoiceStatus(
    invoiceId: string,
    status: "draft" | "issued" | "partially_paid" | "paid" | "overdue" | "void",
  ): Promise<{ invoice: InvoiceRow | null; error: unknown }> {
    const patch: Record<string, unknown> = { status };
    if (status === "void") {
      patch.amount_paid = 0;
      patch.balance_due = 0;
      patch.paid_at = null;
    }

    const { data, error } = await supabase
      .from("invoices")
      .update(patch)
      .eq("id", invoiceId)
      .select(`
        id,
        organization_id,
        customer_id,
        job_id,
        quote_id,
        invoice_number,
        status,
        subtotal,
        tax,
        discount,
        total,
        amount_paid,
        balance_due,
        issued_at,
        due_date,
        paid_at,
        notes,
        created_at,
        updated_at,
        jobs:job_id (
          id,
          job_number,
          title
        ),
        customers:customer_id (
          id,
          full_name,
          first_name,
          last_name,
          email
        )
      `)
      ;

    return { invoice: (data?.[0] as InvoiceRow) ?? null, error };
  },

  async updateInvoiceAmount(
    invoiceId: string,
    amount: number,
  ): Promise<{ invoice: InvoiceRow | null; error: unknown }> {
    const nextAmount = Number(amount);
    if (!Number.isFinite(nextAmount) || nextAmount <= 0) {
      return { invoice: null, error: new Error("Invoice amount must be greater than zero.") };
    }

    const { data: existingInvoice, error: existingError } = await supabase
      .from("invoices")
      .select("id, amount_paid, balance_due, total, status")
      .eq("id", invoiceId);

    const existingInvoiceRow = existingInvoice?.[0] ?? null;

    if (existingError || !existingInvoiceRow) {
      return { invoice: null, error: existingError ?? new Error("Invoice not found.") };
    }

    if (Number(existingInvoiceRow.amount_paid ?? 0) > 0) {
      return { invoice: null, error: new Error("Cannot edit an invoice after payment has been received.") };
    }

    const { count: paymentCount, error: paymentCountError } = await supabase
      .from("payments")
      .select("id", { head: true, count: "exact" })
      .eq("invoice_id", invoiceId);

    if (paymentCountError) {
      return { invoice: null, error: paymentCountError };
    }

    if ((paymentCount ?? 0) > 0) {
      return { invoice: null, error: new Error("Cannot edit an invoice after payment has been received.") };
    }

    const { data, error } = await supabase
      .from("invoices")
      .update({
        subtotal: nextAmount,
        total: nextAmount,
        balance_due: nextAmount,
      })
      .eq("id", invoiceId)
      .select(`
        id,
        organization_id,
        customer_id,
        job_id,
        quote_id,
        invoice_number,
        status,
        subtotal,
        tax,
        discount,
        total,
        amount_paid,
        balance_due,
        issued_at,
        due_date,
        paid_at,
        notes,
        created_at,
        updated_at,
        jobs:job_id (
          id,
          job_number,
          title
        ),
        customers:customer_id (
          id,
          full_name,
          first_name,
          last_name,
          email
        )
      `)
      ;

    if (error || !data || data.length === 0) {
      return { invoice: null, error: error ?? new Error("Failed to update invoice amount.") };
    }

    await supabase
      .from("invoice_items")
      .update({
        unit_price: nextAmount,
        line_total: nextAmount,
      })
      .eq("invoice_id", invoiceId);

    return { invoice: data[0] as InvoiceRow, error: null };
  },

  async reversePaymentsAndVoidInvoice(
    invoiceId: string,
  ): Promise<{ invoice: InvoiceRow | null; removedPayments: number; error: unknown }> {
    const { data: invoiceRow, error: invoiceError } = await supabase
      .from("invoices")
      .select("id, total")
      .eq("id", invoiceId)
      .maybeSingle();

    if (invoiceError || !invoiceRow) {
      return { invoice: null, removedPayments: 0, error: invoiceError ?? new Error("Invoice not found.") };
    }

    const { data: existingPayments, error: listError } = await supabase
      .from("payments")
      .select("id")
      .eq("invoice_id", invoiceId);
    if (listError) {
      return { invoice: null, removedPayments: 0, error: listError };
    }

    const paymentIds = (existingPayments ?? []).map((row) => row.id as string);
    if (paymentIds.length > 0) {
      const { error: deleteError } = await supabase
        .from("payments")
        .delete()
        .in("id", paymentIds);
      if (deleteError) {
        return { invoice: null, removedPayments: 0, error: deleteError };
      }
    }

    const { data, error } = await supabase
      .from("invoices")
      .update({
        status: "void",
        amount_paid: 0,
        balance_due: 0,
        paid_at: null,
      })
      .eq("id", invoiceId)
      .select(`
        id,
        organization_id,
        customer_id,
        job_id,
        quote_id,
        invoice_number,
        status,
        subtotal,
        tax,
        discount,
        total,
        amount_paid,
        balance_due,
        issued_at,
        due_date,
        paid_at,
        notes,
        created_at,
        updated_at,
        jobs:job_id (
          id,
          job_number,
          title
        ),
        customers:customer_id (
          id,
          full_name,
          first_name,
          last_name,
          email
        )
      `)
      ;

    return { invoice: (data?.[0] as InvoiceRow) ?? null, removedPayments: paymentIds.length, error };
  },

  async recordPayment(input: {
    invoiceId: string;
    amount: number;
    method: PaymentMethod;
    reference?: string | null;
    notes?: string | null;
    paidAt?: string;
  }): Promise<{ payment: PaymentRow | null; invoice: InvoiceRow | null; error: unknown }> {
    const amount = Number(input.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return { payment: null, invoice: null, error: new Error("Payment amount must be greater than zero.") };
    }

    const { data: invoice, error: invoiceError } = await supabase
      .from("invoices")
      .select("id, organization_id, customer_id, balance_due, amount_paid")
      .eq("id", input.invoiceId);

    const invoiceRow = invoice?.[0] ?? null;

    if (invoiceError || !invoiceRow) {
      return { payment: null, invoice: null, error: invoiceError ?? new Error("Invoice not found.") };
    }

    const boundedAmount = Math.min(amount, Number(invoiceRow.balance_due ?? amount));
    if (!Number.isFinite(boundedAmount) || boundedAmount <= 0) {
      return {
        payment: null,
        invoice: null,
        error: new Error("Invoice balance is already fully paid."),
      };
    }

    const { data: payment, error: paymentError } = await supabase
      .from("payments")
      .insert({
        organization_id: invoiceRow.organization_id,
        invoice_id: invoiceRow.id,
        customer_id: invoiceRow.customer_id,
        amount: boundedAmount,
        method: input.method,
        reference: input.reference?.trim() || null,
        notes: input.notes?.trim() || null,
        paid_at: input.paidAt ?? new Date().toISOString(),
      })
      .select(
        "id, organization_id, invoice_id, customer_id, payment_number, amount, method, reference, paid_at, notes, created_at, updated_at"
      )
      .single();

    if (paymentError || !payment) {
      return { payment: null, invoice: null, error: paymentError ?? new Error("Failed to record payment.") };
    }

    // Update invoice to reflect the payment — no DB trigger exists to do this automatically.
    const newAmountPaid = Number(invoiceRow.amount_paid ?? 0) + boundedAmount;
    const newBalanceDue = Math.max(0, Number(invoiceRow.balance_due ?? 0) - boundedAmount);
    const isFullyPaid = newBalanceDue <= 0;
    const paidAt = input.paidAt ?? new Date().toISOString();
    await supabase
      .from("invoices")
      .update({
        amount_paid: newAmountPaid,
        balance_due: newBalanceDue,
        status: isFullyPaid ? "paid" : (Number(invoiceRow.amount_paid ?? 0) > 0 ? "partially_paid" : "issued"),
        ...(isFullyPaid ? { paid_at: paidAt } : {}),
      })
      .eq("id", input.invoiceId);

    const { data: updatedInvoice, error: updatedInvoiceError } = await supabase
      .from("invoices")
      .select(`
        id,
        organization_id,
        customer_id,
        job_id,
        quote_id,
        invoice_number,
        status,
        subtotal,
        tax,
        discount,
        total,
        amount_paid,
        balance_due,
        issued_at,
        due_date,
        paid_at,
        notes,
        created_at,
        updated_at,
        jobs:job_id (
          id,
          job_number,
          title
        ),
        customers:customer_id (
          id,
          full_name,
          first_name,
          last_name,
          email
        )
      `)
      .eq("id", input.invoiceId);

    return {
      payment: payment as PaymentRow,
      invoice: (updatedInvoice?.[0] as InvoiceRow) ?? null,
      error: updatedInvoiceError ?? null,
    };
  },

  async getKpis(): Promise<{ data: AccountingKpis | null; error: unknown }> {
    const [jobsTotal, jobsActive, leadsTotal, leadsRejected, quotesTotal, quotesWon, invoices] = await Promise.all([
      supabase.from("jobs").select("id", { head: true, count: "exact" }),
      supabase.from("jobs").select("id", { head: true, count: "exact" }).neq("status", "completed"),
      supabase.from("leads").select("id", { head: true, count: "exact" }),
      supabase.from("leads").select("id", { head: true, count: "exact" }).eq("status", "rejected"),
      supabase.from("quotes").select("id", { head: true, count: "exact" }),
      supabase.from("quotes").select("id,total", { count: "exact" }).eq("status", "Closed Won"),
      supabase.from("invoices").select("id,status,total,balance_due", { count: "exact" }),
    ]);

    const error = jobsTotal.error || jobsActive.error || leadsTotal.error || leadsRejected.error || quotesTotal.error || quotesWon.error || invoices.error;
    if (error) return { data: null, error };

    const wonQuotesRows = (quotesWon.data ?? []) as Array<{ total: number | null }>;
    const invoiceRows = (invoices.data ?? []) as Array<{ status: InvoiceStatus; total: number; balance_due: number }>;

    const totalLeads = leadsTotal.count ?? 0;
    const rejectedLeads = leadsRejected.count ?? 0;
    const avgRejectRate = totalLeads > 0 ? (rejectedLeads / totalLeads) * 100 : 0;

    const openInvoices = invoiceRows.filter((row) => row.status !== "paid" && row.status !== "void").length;
    const overdueInvoices = invoiceRows.filter((row) => row.status === "overdue").length;
    const invoiceRevenue = invoiceRows
      .filter((row) => row.status !== "void")
      .reduce((sum, row) => sum + Number(row.total || 0), 0);
    const outstandingBalance = invoiceRows
      .filter((row) => row.status !== "void")
      .reduce((sum, row) => sum + Number(row.balance_due || 0), 0);

    return {
      data: {
        totalJobs: jobsTotal.count ?? 0,
        activeJobs: jobsActive.count ?? 0,
        totalLeads,
        rejectedLeads,
        avgRejectRate,
        totalQuotes: quotesTotal.count ?? 0,
        wonQuotes: quotesWon.count ?? 0,
        wonQuoteRevenue: wonQuotesRows.reduce((sum, row) => sum + Number(row.total || 0), 0),
        totalInvoices: invoices.count ?? 0,
        openInvoices,
        overdueInvoices,
        invoiceRevenue,
        outstandingBalance,
      },
      error: null,
    };
  },
};
